import React from 'react';
import { Helmet } from '@lib/helmet-shim';
import { getRobotsMeta, SEO_CONFIG, SUPPORTED_LANGUAGES, SITE_ORIGIN, HOME_CANONICAL_URL, siteConfig, toAbsoluteUrl } from '../config';

interface SeoHeadProps {
  title?: string;
  description?: string;
  pageSlug?: string; // 'terms', 'dmca', 'privacy', etc.
  image?: string;
  imageWidth?: number;
  imageHeight?: number;
  url?: string;
  canonicalUrl?: string;
  jsonLd?: Record<string, unknown> | Array<Record<string, unknown> | null | undefined>;
  /** ISO 8601 date string for article:published_time */
  publishedTime?: string;
  /** ISO 8601 date string for article:modified_time */
  modifiedTime?: string;
}

/**
 * SEO Head Component
 * Handles all meta tags including dynamic robots meta for different page types
 * Automatically applies noindex,nofollow for terms/dmca/privacy pages
 */
const SeoHead: React.FC<SeoHeadProps> = ({
  title,
  description,
  pageSlug,
  image = SEO_CONFIG.DEFAULT_IMAGE,
  imageWidth = 1200,
  imageHeight = 630,
  url,
  canonicalUrl,
  jsonLd,
  publishedTime,
  modifiedTime,
}) => {
  const robotsMeta = getRobotsMeta(pageSlug);
  const fullTitle = title
    ? `${title}${SEO_CONFIG.TITLE_SEPARATOR}${SEO_CONFIG.TITLE_SUFFIX}`
    : undefined;
  // Canonical and JSON-LD homepage URLs share HOME_CANONICAL_URL / toAbsoluteUrl
  const canonical = toAbsoluteUrl(canonicalUrl || url || '/');

  // Resolve absolute image URL from the same origin
  const imageUrl = image.startsWith('http') ? image : `${SITE_ORIGIN}${image.startsWith('/') ? image : `/${image}`}`;
  const jsonLdScripts = (Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [])
    .filter((schema): schema is Record<string, unknown> => Boolean(schema));

  const isHomepage = canonical === HOME_CANONICAL_URL;
  const canonicalPath = canonical.replace(/\/$/, '');

  return (
    <Helmet>
      {fullTitle && <title>{fullTitle}</title>}
      {description && <meta name="description" content={description} />}
      <meta name="robots" content={robotsMeta} />
      <link rel="canonical" href={canonical} />

      {/* hreflang alternate links — signals multilingual content to Google */}
      {SUPPORTED_LANGUAGES.map(({ hreflang }) => {
        const langUrl = hreflang === 'en'
          ? canonical
          : isHomepage
            ? `${SITE_ORIGIN}/${hreflang}`
            : `${canonicalPath}?hl=${hreflang}`;
        return <link key={hreflang} rel="alternate" hrefLang={hreflang} href={langUrl} />;
      })}
      {/* x-default always points to the canonical (English) URL */}
      <link rel="alternate" hrefLang="x-default" href={canonical} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={siteConfig.siteName} />
      {fullTitle && <meta property="og:title" content={fullTitle} />}
      {description && <meta property="og:description" content={description} />}
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:width" content={String(imageWidth)} />
      <meta property="og:image:height" content={String(imageHeight)} />
      <meta property="og:image:alt" content={fullTitle ?? siteConfig.siteName} />
      <meta property="og:locale" content={SEO_CONFIG.OG_LOCALE} />
      <meta property="og:url" content={canonical} />
      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}

      {/* Twitter Card */}
      <meta name="twitter:card" content={SEO_CONFIG.TWITTER_CARD_TYPE} />
      <meta name="twitter:site" content={SEO_CONFIG.TWITTER_HANDLE} />
      {fullTitle && <meta name="twitter:title" content={fullTitle} />}
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:image:alt" content={fullTitle ?? siteConfig.siteName} />

      {/* JSON-LD Structured Data — one script per schema so crawlers can read @type at the root */}
      {jsonLdScripts.map((schema, index) => {
        const schemaType = Array.isArray(schema['@type'])
          ? schema['@type'].join('-')
          : String(schema['@type'] ?? (schema['@graph'] ? 'graph' : index));
        return (
          <script key={`${schemaType}-${index}`} type="application/ld+json" data-schema={schemaType}>
            {JSON.stringify(schema)}
          </script>
        );
      })}
    </Helmet>
  );
};

export default SeoHead;
